import { useParams, Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { useMemo, useEffect, useState } from 'react';
import { Layout } from '@/components/Layout';
import { TorchSvg } from '@/components/torch/TorchSvg';
import { useTorchBySlug } from '@/hooks/useTorchQueries';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import type { TorchState, AcceptLink } from '@/lib/torchTypes';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ArrowLeft, Flame, Zap, Crown, Share2, CheckCircle2, MessageSquareQuote } from 'lucide-react';

function StateBadge({ state }: { state: TorchState }) {
  const variants: Record<TorchState, { emoji: string; label: string; className: string }> = {
    lit:          { emoji: '🔥', label: 'Lit',          className: 'bg-orange-500 hover:bg-orange-600' },
    extinguished: { emoji: '🪦', label: 'Extinguished', className: 'bg-gray-500 hover:bg-gray-600' },
  };
  const v = variants[state];
  return (
    <Badge className={`${v.className} text-white text-sm px-3 py-1`}>
      {v.emoji} {v.label}
    </Badge>
  );
}

function formatTTL(hours: number): string {
  if (hours >= 720) { const m = Math.round(hours / 720); return m === 1 ? '1 month' : `${m} months`; }
  if (hours >= 168) { const w = Math.round(hours / 168); return w === 1 ? '1 week' : `${w} weeks`; }
  if (hours >= 48)  return `${Math.round(hours / 24)} days`;
  if (hours === 24) return '24 hours';
  return `${hours} hours`;
}

function toNpub(pubkey: string) {
  try { return nip19.npubEncode(pubkey); } catch { return ''; }
}

/** Small inline attribution for who sent a message / pass */
function PasserAttribution({ pubkey }: { pubkey: string }) {
  const { data: author, isLoading } = useAuthor(pubkey);
  const metadata = author?.metadata;
  const displayName = metadata?.name ?? metadata?.display_name ?? genUserName(pubkey);
  const picture = metadata?.picture;
  const initials = displayName.slice(0, 2).toUpperCase();
  const npub = useMemo(() => toNpub(pubkey), [pubkey]);

  if (isLoading) return <Skeleton className="h-3 w-20 inline-block" />;

  return (
    <Link to={`/${npub}`} className="inline-flex items-center gap-1 hover:text-orange-400 transition-colors">
      <Avatar className="w-4 h-4 flex-shrink-0">
        <AvatarImage src={picture} alt={displayName} />
        <AvatarFallback className="text-[8px]">{initials}</AvatarFallback>
      </Avatar>
      <span className="font-medium text-foreground/80">{displayName}</span>
    </Link>
  );
}

interface HolderRowProps {
  link: AcceptLink;
  isCurrent: boolean;
  ribbonColor: string;
}

function HolderRow({ link, isCurrent, ribbonColor }: HolderRowProps) {
  const { data: author, isLoading } = useAuthor(link.holderPubkey);
  const metadata = author?.metadata;
  const displayName = metadata?.name ?? metadata?.display_name ?? genUserName(link.holderPubkey);
  const nip05 = metadata?.nip05;
  const picture = metadata?.picture;
  const initials = displayName.slice(0, 2).toUpperCase();
  const isGenesis = link.positionIndex === 0;
  const npub = useMemo(() => toNpub(link.holderPubkey), [link.holderPubkey]);

  const zapTier = link.zapAmountSats >= 10000 ? '🔥 Legendary'
               : link.zapAmountSats >= 1000  ? '⚡ Large'
               : link.zapAmountSats >= 100   ? '✨ Medium'
               : link.zapAmountSats > 0      ? '💧 Small'
               : '';

  return (
    <Card className={isCurrent ? 'border-orange-300 dark:border-orange-700' : ''}>
      <CardContent className="py-4">
        <div className="flex items-start gap-3">
          <div className="w-2 h-2 rounded-full mt-4 flex-shrink-0" style={{ backgroundColor: ribbonColor }} />
          {isLoading
            ? <Skeleton className="w-9 h-9 rounded-full flex-shrink-0" />
            : (
              <Link to={`/${npub}`} className="flex-shrink-0">
                <Avatar className="w-9 h-9 hover:ring-2 hover:ring-orange-400 transition-all">
                  <AvatarImage src={picture} alt={displayName} />
                  <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                </Avatar>
              </Link>
            )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {isLoading
                ? <Skeleton className="h-4 w-28" />
                : <Link to={`/${npub}`} className="font-medium text-sm truncate max-w-[180px] hover:text-orange-400 transition-colors">{displayName}</Link>}
              {isGenesis
                ? <Badge variant="outline" className="text-xs border-yellow-400 text-yellow-600 dark:text-yellow-400 px-1.5 py-0">🔥 Genesis</Badge>
                : <span className="text-xs text-muted-foreground">#{link.positionIndex}</span>}
              {isCurrent && (
                <Badge variant="outline" className="text-xs border-orange-300 px-1.5 py-0">Current</Badge>
              )}
            </div>
            {nip05 && <p className="text-xs text-muted-foreground truncate mt-0.5">{nip05}</p>}
            {!nip05 && !isLoading && (
              <p className="text-xs text-muted-foreground font-mono mt-0.5">
                {link.holderPubkey.slice(0, 8)}…{link.holderPubkey.slice(-6)}
              </p>
            )}
            {!isGenesis && (
              <div className="flex items-center gap-1.5 mt-1.5 text-xs text-muted-foreground">
                <Zap className="w-3 h-3" />
                <span>{link.zapAmountSats.toLocaleString()} sats</span>
                {zapTier && <><span>·</span><span>{zapTier}</span></>}
              </div>
            )}

            {/* Pass message — attributed to the passer, shown below the recipient info */}
            {link.mark && link.passerPubkey && (
              <div className="mt-3 rounded-lg border border-border/60 bg-muted/40 px-3 py-2.5">
                <div className="flex items-start gap-2">
                  <MessageSquareQuote className="w-3.5 h-3.5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm italic text-foreground/90 leading-snug">"{link.mark}"</p>
                    <p className="text-xs text-muted-foreground mt-1.5">
                      — <PasserAttribution pubkey={link.passerPubkey} />{' '}
                      <span className="opacity-60">when passing</span>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CurrentHolderCard({ pubkey }: { pubkey: string }) {
  const { data: author, isLoading } = useAuthor(pubkey);
  const metadata = author?.metadata;
  const displayName = metadata?.name ?? metadata?.display_name ?? genUserName(pubkey);
  const nip05 = metadata?.nip05;
  const picture = metadata?.picture;
  const initials = displayName.slice(0, 2).toUpperCase();
  const npub = useMemo(() => toNpub(pubkey), [pubkey]);

  return (
    <Link to={`/${npub}`} className="flex items-center gap-3 group">
      <div className="relative">
        {isLoading
          ? <Skeleton className="w-10 h-10 rounded-full" />
          : (
            <Avatar className="w-10 h-10 ring-2 ring-orange-400 ring-offset-2 group-hover:ring-orange-300 transition-all">
              <AvatarImage src={picture} alt={displayName} />
              <AvatarFallback className="text-sm font-semibold">{initials}</AvatarFallback>
            </Avatar>
          )}
        <div className="absolute -top-1 -right-1 bg-orange-500 rounded-full p-0.5">
          <Crown className="w-2.5 h-2.5 text-white" />
        </div>
      </div>
      <div className="min-w-0">
        {isLoading
          ? <><Skeleton className="h-4 w-24 mb-1" /><Skeleton className="h-3 w-16" /></>
          : (
            <>
              <p className="font-semibold text-sm leading-tight truncate group-hover:text-orange-400 transition-colors">{displayName}</p>
              <p className="text-xs text-muted-foreground truncate">
                {nip05 || `${pubkey.slice(0, 8)}…${pubkey.slice(-6)}`}
              </p>
            </>
          )}
      </div>
    </Link>
  );
}

function LiveCountdown({ expiresAt }: { expiresAt: number }) {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, []);

  const remaining = Math.max(0, expiresAt - now);
  const d = Math.floor(remaining / 86400);
  const h = Math.floor((remaining % 86400) / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  const s = remaining % 60;

  if (remaining === 0) return <div className="text-2xl font-mono font-bold text-yellow-600">Dormant</div>;

  const isUrgent = remaining < 3600;
  const colorClass = isUrgent ? 'text-red-500 animate-pulse' : 'text-orange-600';

  if (d > 0) return <div className={`text-2xl font-mono font-bold ${colorClass}`}>{d}d {h}h {m}m</div>;
  if (h > 0) return <div className={`text-2xl font-mono font-bold ${colorClass}`}>{h}h {m}m {s}s</div>;
  return <div className={`text-2xl font-mono font-bold ${colorClass}`}>{m}m {s}s</div>;
}

export function TorchDetail() {
  const { naddr } = useParams<{ naddr: string }>();
  const { data: parsedChain, isLoading } = useTorchBySlug(naddr || '');
  const { user } = useCurrentUser();
  const [shareCopied, setShareCopied] = useState(false);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: parsedChain?.genesis.title || 'Torchlite', url }); }
      catch { await navigator.clipboard.writeText(url); setShareCopied(true); setTimeout(() => setShareCopied(false), 2000); }
    } else {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    }
  };

  const state: TorchState = parsedChain?.state ?? 'lit';

  // Build the visual chain: genesis creator is position 0, then each pass recipient
  const chain: AcceptLink[] = useMemo(() => {
    const passLinks: AcceptLink[] = (parsedChain?.canonicalChain ?? []).map(link => ({
      holderPubkey: link.pass.recipientPubkey,
      passerPubkey: link.pass.pubkey,
      zapAmountSats: link.pass.zapAmountSats,
      mark: link.pass.mark,
      positionIndex: link.position,
    }));

    if (!parsedChain?.genesis) return passLinks;

    const genesisLink: AcceptLink = {
      holderPubkey: parsedChain.genesis.pubkey,
      passerPubkey: undefined,
      zapAmountSats: 0,
      mark: undefined,
      positionIndex: 0,
    };

    return [genesisLink, ...passLinks];
  }, [parsedChain]);

  const genesis = parsedChain?.genesis;
  const expiresAt = parsedChain?.expiresAt ?? 0;
  const currentHolderPubkey = parsedChain?.currentHolder ?? genesis?.pubkey ?? null;
  const isCurrentHolder = user && currentHolderPubkey === user.pubkey;

  if (isLoading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Skeleton className="w-64 h-80 mx-auto" />
            <div className="space-y-4">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!parsedChain || !genesis) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <Flame className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Torch Not Found</h1>
          <p className="text-muted-foreground mb-4">This torch does not exist or could not be loaded from relays.</p>
          <Link to="/"><Button>← Back</Button></Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">

        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Link>

        {/* Prompt genesis creator to pass immediately */}
        {user && user.pubkey === genesis.pubkey && chain.length <= 1 && state === 'lit' && (
          <Card className="border-orange-400 bg-orange-50 dark:bg-orange-950/30">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <Flame className="w-5 h-5 text-orange-500 shrink-0" />
                  <div>
                    <p className="font-semibold text-orange-800 dark:text-orange-200">🎉 Your torch is live!</p>
                    <p className="text-xs text-orange-600 dark:text-orange-400">Zap someone to start the chain.</p>
                  </div>
                </div>
                <Link to={`/pass/${genesis.slug}`}>
                  <Button className="bg-orange-500 hover:bg-orange-600 shrink-0">
                    <Zap className="w-4 h-4 mr-2" />
                    Pass It Now
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Hero */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div className="flex justify-center">
            <TorchSvg
              genesisId={genesis.id}
              chain={chain}
              state={state}
              className="w-56 h-72 md:w-72 md:h-96"
            />
          </div>

          <div className="space-y-5">
            <div>
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h1 className="text-3xl font-bold">{genesis.title}</h1>
                <StateBadge state={state} />
              </div>
              {genesis.description && <p className="text-muted-foreground">{genesis.description}</p>}
            </div>

            {/* Current holder */}
            {currentHolderPubkey && state === 'lit' && (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 border border-border/50">
                <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide shrink-0">
                  Held by
                </span>
                <CurrentHolderCard pubkey={currentHolderPubkey} />
              </div>
            )}

            {/* State-specific info */}
            {state === 'lit' && (
              <Card className="border-orange-200 bg-orange-50 dark:bg-orange-950/20">
                <CardContent className="pt-5 pb-4">
                  <p className="text-sm text-orange-800 dark:text-orange-200 mb-1">⏱ Extinguishes in</p>
                  <LiveCountdown expiresAt={expiresAt} />
                </CardContent>
              </Card>
            )}

            {state === 'extinguished' && (
              <Card className="border-gray-200 bg-gray-50 dark:bg-gray-900/20">
                <CardContent className="pt-5 pb-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">🪦 Extinguished</p>
                  <p className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                    The flame died after {chain.length} holder{chain.length !== 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    The chain is sealed forever on Nostr.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Pass CTA — only for current holder while lit */}
            <div className="space-y-2">
              {state === 'lit' && isCurrentHolder && (
                <Link to={`/pass/${genesis.slug}`} className="block">
                  <Button className="w-full bg-orange-500 hover:bg-orange-600">
                    <Zap className="w-4 h-4 mr-2" />
                    Pass the Torch
                  </Button>
                </Link>
              )}
              {state === 'lit' && !isCurrentHolder && (
                <p className="text-sm text-muted-foreground text-center py-2 px-4 bg-muted rounded-lg">
                  {user ? 'You are not the current holder of this torch.' : 'Log in to see if this torch is yours to pass.'}
                </p>
              )}
              <Button variant="outline" className="w-full gap-2" onClick={handleShare}>
                {shareCopied
                  ? <><CheckCircle2 className="w-4 h-4 text-green-500" /> Copied!</>
                  : <><Share2 className="w-4 h-4" /> Share Torch</>}
              </Button>
            </div>
          </div>
        </div>

        <Separator />

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6 text-center">
              <div className="text-3xl font-bold">{chain.length}</div>
              <p className="text-sm text-muted-foreground">Total Holders</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <div className="text-3xl font-bold">
                {(() => {
                  const total = chain.reduce((acc, l) => acc + l.zapAmountSats, 0);
                  return total >= 1000 ? `${Math.floor(total / 1000)}k` : total;
                })()}
              </div>
              <p className="text-sm text-muted-foreground">Total Sats Zapped</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <div className="text-3xl font-bold">{formatTTL(genesis.ttlHours)}</div>
              <p className="text-sm text-muted-foreground">Time to Pass</p>
            </CardContent>
          </Card>
        </div>

        {/* Lineage */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Lineage</h2>
          <div className="space-y-3">
            {[...chain].reverse().map((link) => (
              <HolderRow
                key={link.positionIndex}
                link={link}
                isCurrent={link.positionIndex === chain.length - 1}
                ribbonColor={`hsl(${parseInt(link.holderPubkey.slice(0, 2), 16) * 360 / 256}, 70%, 55%)`}
              />
            ))}
          </div>
        </div>

      </div>
    </Layout>
  );
}
